import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { getUserRestaurant } from '../utils/restaurantUtils';
import AdminNavigation from '../components/AdminNavigation';
import WaiterCallsPanel from '../components/WaiterCallsPanel';
import { 
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  Loader,
  Euro
} from 'lucide-react';

function Dashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;
  const [stats, setStats] = useState({
    todayOrders: 0,
    pendingOrders: 0,
    todayRevenue: 0,
    avgOrderValue: 0
  });

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      const resId = await getUserRestaurant(auth.currentUser.uid);
      if (resId) {
        setRestaurantId(resId);
      } else {
        // Fallback for demo user
        setRestaurantId('demo-restaurant');
      }
    };
    fetchRestaurant();
  }, [navigate]);

  useEffect(() => {
    if (!restaurantId) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const ordersQuery = query(
      collection(db, 'restaurants', restaurantId, 'orders'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(ordersData);
      setLoading(false);

      // Calculate stats for today only
      const todayOrders = ordersData.filter(o => {
        const orderDate = o.createdAt?.toDate?.();
        return orderDate && orderDate >= today;
      });
      
      const pendingOrders = todayOrders.filter(o => o.status === 'pending').length;
      const todayRevenue = todayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const avgOrderValue = todayOrders.length > 0 ? todayRevenue / todayOrders.length : 0;

      setStats({
        todayOrders: todayOrders.length,
        pendingOrders,
        todayRevenue,
        avgOrderValue
      });
    });

    return () => unsubscribe();
  }, [restaurantId]);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      preparing: 'badge-preparing',
      ready: 'badge-ready',
      completed: 'badge-completed'
    };
    return badges[status] || 'badge-pending';
  };

  const getOrderRowColor = (status) => {
    const colors = {
      pending: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
      preparing: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      ready: 'bg-green-50 border-green-200 hover:bg-green-100',
      completed: 'bg-gray-50 border-gray-200 hover:bg-gray-100'
    };
    return colors[status] || 'bg-white border-gray-200 hover:bg-gray-50';
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    // Exclude completed orders from recent orders view
    if (order.status === 'completed') return false;
    if (filter === 'all') return true;
    return order.status === filter;
  });

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Waiter Calls Panel - Always visible when there are calls */}
        {restaurantId && <WaiterCallsPanel restaurantId={restaurantId} />}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Recent Orders - 70% on desktop */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <div className="card">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-4">
                <h2 className="text-lg md:text-xl font-bold text-gray-900">Recent Orders</h2>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'pending', 'preparing', 'ready'].map(status => {
                    const count = status === 'all' 
                      ? orders.filter(o => o.status !== 'completed').length
                      : orders.filter(o => o.status === status).length;
                    
                    return (
                      <button
                        key={status}
                        onClick={() => {
                          setFilter(status);
                          setCurrentPage(1);
                        }}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                          filter === status
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        <span className="ml-1 text-[10px] sm:text-xs">
                          ({count})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-600">Loading orders...</p>
                </div>
              ) : currentOrders.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    {filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {currentOrders.map(order => (
                      <div
                        key={order.id}
                        onClick={() => navigate('/orders')}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 border rounded-lg transition-colors cursor-pointer ${getOrderRowColor(order.status)}`}
                      >
                        <div className="flex-1 mb-2 sm:mb-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">
                              Table {order.tableId}
                            </span>
                            <span className={`badge ${getStatusBadge(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {order.items?.length || 0} items • {order.createdAt?.toDate?.().toLocaleString([], { 
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-lg font-bold text-gray-900">€{order.total?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </button>
                      
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Stats Grid - 30% on desktop, stacked vertically */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Today's Orders</p>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats.todayOrders}</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full">
                    <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Pending Orders</p>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats.pendingOrders}</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full">
                    <Clock className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Today's Revenue</p>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">€{stats.todayRevenue.toFixed(0)}</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full">
                    <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Avg Order Value</p>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">€{stats.avgOrderValue.toFixed(0)}</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full">
                    <Euro className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
